import { ChronoType, type Chrono } from "@/modules/chrono/chronoTypes"
import { convertChronoFromDto, convertChronoToDto } from "@/modules/chrono/services/chronoDtos"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type { ScoreboardScoreEntity } from "@/modules/scores/scoreForms/entities/scoreboardScoreEntity"
import { ScoreResultType, ScoreType, type ScoreSetConfiguration } from "@/modules/scores/scoreTypes"
import type { ExerciseDto, ExerciseInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import {
  ProgramExerciseTypeOf,
  type ExerciseCategory,
} from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseTypes"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { EditExerciseTemplateInputDto } from "@/modules/template/gateway/templateDto"

export class SessionBlockExerciseEntity {
  id: string
  _type: typeof ProgramExerciseTypeOf
  title: string
  description: string
  category: ExerciseCategory
  otherCategory: string | null
  chrono: Chrono | null
  isFavorite: boolean
  creator: UserEntity | null
  rpe: number | null
  requiredMedia: boolean
  durationInMin: number | null
  orderIndex: number
  isCompleted: boolean
  score: ScoreboardScoreEntity | null
  scoreSetConfigurations: ScoreSetConfiguration[]
  scoringOption: ScoreResultType | null
  scoreType: ScoreType

  private constructor(sessionBlockExercise: SessionBlockExerciseEntity) {
    this.id = sessionBlockExercise.id
    this._type = sessionBlockExercise._type
    this.title = sessionBlockExercise.title
    this.description = sessionBlockExercise.description
    this.category = sessionBlockExercise.category
    this.otherCategory = sessionBlockExercise.otherCategory
    this.chrono = sessionBlockExercise.chrono
    this.isFavorite = sessionBlockExercise.isFavorite
    this.creator = sessionBlockExercise.creator
    this.rpe = sessionBlockExercise.rpe
    this.requiredMedia = sessionBlockExercise.requiredMedia
    this.durationInMin = sessionBlockExercise.durationInMin
    this.orderIndex = sessionBlockExercise.orderIndex
    this.isCompleted = sessionBlockExercise.isCompleted
    this.score = sessionBlockExercise.score
    this.scoreSetConfigurations = sessionBlockExercise.scoreSetConfigurations
    this.scoringOption = sessionBlockExercise.scoringOption
    this.scoreType = sessionBlockExercise.scoreType
  }

  static fromDto(dto: ExerciseDto): SessionBlockExerciseEntity {
    if (dto.type !== ProgramExerciseTypeOf) throw new Error("Invalid exercise type")

    return new SessionBlockExerciseEntity({
      id: dto.id,
      _type: dto.type,
      title: dto.title,
      description: dto.description,
      category: dto.category,
      otherCategory: dto.otherCategory,
      chrono: dto.chrono ? convertChronoFromDto(dto.chrono) : null,
      isFavorite: dto.isFavorite,
      creator: dto.owner ? UserEntity.fromDto(dto.owner) : null,
      rpe: dto.rpe,
      requiredMedia: dto.requiredMedia,
      durationInMin: dto.durationInMin,
      orderIndex: dto.orderIndex,
      isCompleted: dto.isCompleted,
      score: dto.score,
      scoreSetConfigurations: dto.scoreSetConfigurations.sort((a, b) => a.displayOrder - b.displayOrder),
      scoringOption: dto.scoringOption,
      scoreType: dto.scoreType,
    })
  }

  // TODO CREATED NEW FUNCTION FOR AND APPLY
  // static fromEntityTemplateToFormik(entity: SessionBlockExerciseEntity): ExerciseFormikType {
  //   return {
  //     id: template.id,
  //     title: template.name,
  //     description: template.description,
  //     category: template.category,
  //     otherCategory: template.otherCategory,
  //   }
  // }

  static fromEntityToFormik(entity: SessionBlockExerciseEntity): ExerciseFormikType {
    return {
      id: entity.id,
      title: entity.title,
      description: entity.description,
      category: entity.category,
      otherCategory: entity.otherCategory,
      chrono: entity.chrono,
      chronoType: entity.chrono?.type ?? "NONE",
      chronoForTime: entity.chrono?.type === ChronoType.FOR_TIME ? entity.chrono : null,
      chronoTabata: entity.chrono?.type === ChronoType.TABATA ? entity.chrono : null,
      chronoEmom: entity.chrono?.type === ChronoType.EMOM ? entity.chrono : null,
      chronoAmrap: entity.chrono?.type === ChronoType.AMRAP ? entity.chrono : null,
      rpe: entity.rpe,
      requiredMedia: entity.requiredMedia,
      durationInMin: entity.durationInMin,
      scoreSetConfigurations: entity.scoreSetConfigurations,
      scoringOption:
        entity.scoringOption === ScoreResultType.SET_N_FOR_SCOREBOARD
          ? entity.scoreSetConfigurations.findIndex((scoreSet) => scoreSet.useForScoring) >= 0
            ? `SET_N_FOR_SCOREBOARD_${String(entity.scoreSetConfigurations.findIndex((scoreSet) => scoreSet.useForScoring))}`
            : null
          : entity.scoringOption,
      scoreType: entity.scoreType,
    }
  }

  static fromEntityToInputDto(entity: SessionBlockExerciseEntity): ExerciseInputDto {
    return {
      id: entity.id,
      title: entity.title,
      description: entity.description,
      category: entity.category,
      otherCategory: entity.category === "OTHER" ? entity.otherCategory : null,
      chrono: entity.chrono ? convertChronoToDto(entity.chrono) : null,
      scoreSetConfigurations: entity.scoreSetConfigurations,
      scoringOption: entity.scoringOption,
      scoreType: entity.scoreType,
      orderIndex: entity.orderIndex,
      rpe: entity.rpe,
      requiredMedia: entity.requiredMedia,
      durationInMin: entity.durationInMin,
    }
  }

  static fromFormikToTemplateInputDto(formik: ExerciseFormikType): EditExerciseTemplateInputDto {
    return {
      description: formik.description,
      category: formik.category,
      otherCategory: formik.category === "OTHER" ? formik.otherCategory : null,
      chrono: formik.chrono ? convertChronoToDto(formik.chrono) : null,
      scoreSetConfigurations:
        formik.scoreSetConfigurations.length > 0
          ? formik.scoreSetConfigurations.map((scoreSet, index) => ({
              ...scoreSet,
              useForScoring: formik.scoringOption?.startsWith(`SET_N_FOR_SCOREBOARD_${String(index)}`) ? true : false,
            }))
          : null,
      scoringOption:
        formik.scoringOption === ScoreResultType.AVERAGE ||
        formik.scoringOption === ScoreResultType.TOTAL ||
        formik.scoringOption === ScoreResultType.HIGHEST ||
        formik.scoringOption === ScoreResultType.LOWEST
          ? formik.scoringOption
          : formik.scoringOption
            ? ScoreResultType.SET_N_FOR_SCOREBOARD
            : null,
      rpe: formik.rpe,
      requiredMedia: formik.requiredMedia,
      durationInMin: formik.durationInMin,
      scoreType: formik.scoreType,
    }
  }

  static fromFormikToInputDto(formik: ExerciseFormikType, orderIndex: number): ExerciseInputDto {
    return {
      id: formik.id,
      title: formik.title,
      description: formik.description,
      category: formik.category,
      otherCategory: formik.category === "OTHER" ? formik.otherCategory : null,
      chrono: formik.chrono ? convertChronoToDto(formik.chrono) : null,
      scoreSetConfigurations:
        formik.scoreSetConfigurations.length > 0
          ? formik.scoreSetConfigurations.map((scoreSet, index) => ({
              ...scoreSet,
              useForScoring: formik.scoringOption?.startsWith(`SET_N_FOR_SCOREBOARD_${String(index)}`) ? true : false,
            }))
          : null,
      scoringOption:
        formik.scoringOption === ScoreResultType.AVERAGE ||
        formik.scoringOption === ScoreResultType.TOTAL ||
        formik.scoringOption === ScoreResultType.HIGHEST ||
        formik.scoringOption === ScoreResultType.LOWEST
          ? formik.scoringOption
          : formik.scoringOption
            ? ScoreResultType.SET_N_FOR_SCOREBOARD
            : null,
      rpe: formik.rpe,
      requiredMedia: formik.requiredMedia,
      durationInMin: formik.durationInMin,
      orderIndex: orderIndex,
      scoreType: formik.scoreType,
    }
  }
}
